import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { getTimeLeftInDay, type IState } from "../helpers";
import { DRAINING, isEmergencyResponseActive, type TUnderlyingTrajectory } from "../exemptions/emergencyResponse";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

export type RestConditions = {
    avail: number | undefined;
    isConsuming: boolean;
    restBreakMinimum: number | undefined;
    isOnDutyCountTowardRest: boolean;
    consecutiveBreakTime: number | undefined;
    /** Log-time start of the break in progress, already set on the log that begins it. */
    breakStartTime: Date | undefined;
    /** The break length that will actually credit, which earlyRestBreaks can shorten. */
    effectiveBreakMinimum: number | undefined;
    /** The value a credit restores Rest to; at or above it, crediting changes nothing. */
    restBreakMaximum: number | undefined;
};

/**
 * The break length that will actually credit for this driver.
 *
 * `countRestBreaks` normally credits at `config.rest.restBreakMinimum`, but rulesets carrying
 * `earlyRestBreaks` (Alberta) credit a shorter break when little time has passed since the last
 * one — 10 minutes under a 4 h gap, against the standard 30. The engine measures that gap from
 * `state.current.dateTime` to `endOfLastBreak`, and the iteration that decides it is the one
 * whose current log IS the break's own log, so `breakStartTime` is the right end of the
 * measurement and the answer is fixed for the life of the break.
 */
const getEffectiveBreakMinimum = (state: IState, breakStartTime: Date): number | undefined => {
    const restConfig = state.config.rest;
    if (!restConfig?.restBreakMinimum) {
        return undefined;
    }
    return (restConfig.earlyRestBreaks ?? []).reduce((shortest, early) => {
        // Mirrors countRestBreaks' hasExceededBetweenRestLimit, including its treatment of a
        // missing endOfLastBreak as "not exceeded".
        const hasExceeded = state.workday.endOfLastBreak !== undefined
            && breakStartTime.getTime() - state.workday.endOfLastBreak.getTime() > early.betweenRestLimit;
        return hasExceeded ? shortest : Math.min(shortest, early.restBreakMinimum);
    }, restConfig.restBreakMinimum);
};

/**
 * Whether the Emergency Response floor is currently governing this type — the exemption is running
 * and the raw value is under the floor, which is exactly when getEmergencyResponseProjection
 * returns a projection and compute() overrides isConsuming to true.
 *
 * Deliberately a cheap predicate over state rather than a second call to
 * getEmergencyResponseProjection: conditions() is not memoised, and the projection is already
 * computed once per compute() by the base class.
 */
const floorGoverns = (state: IState, avail: number | undefined): boolean =>
    avail !== undefined
    && isEmergencyResponseActive(state.emergencyResponsePeriods, state.nextDate)
    && avail < getTimeLeftInDay(state);

export class RestCalculator extends AvailabilityCalculator {
    private conditions(state: IState): RestConditions {
        const avail = discreteAvailabilityFunctions.Rest(state, false);
        const isOnDutyCountTowardRest = state.config.rest?.onDutyCountTowardRest === true;
        const consecutiveBreakTime = isOnDutyCountTowardRest
            ? state.workday.consecutiveRestCount
            : state.workday.consecutiveOffCount;
            
        // The counters above are still zero for the one millisecond in which the break begins,
        // because state.duration is zero on the log that starts it. The anchors are already set
        // on that same log (hosCalculator's countOffDutyTime and countOnDutyTime), and they do
        // not move again for the life of the break — even where countDailyRestBreaks subtracts
        // from the counter — so they, not the counters, are what a prediction can be built on.
        const breakStartTime = isOnDutyCountTowardRest
            ? state.workday.consecutiveRestStartTime
            : state.workday.consecutiveOffStartTime;

        return {
            avail,
            isConsuming: state.current?.status === "D",
            restBreakMinimum: state.config.rest?.restBreakMinimum,
            isOnDutyCountTowardRest,
            consecutiveBreakTime,
            breakStartTime,
            effectiveBreakMinimum: breakStartTime === undefined ? undefined : getEffectiveBreakMinimum(state, breakStartTime),
            restBreakMaximum: state.config.rest?.betweenRestLimit,
        };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        return this.conditions(state).isConsuming;
    }

    /**
     * `isConsuming` reports driving, and where the break clock is `timeUntilMustRest`
     * (onDutyCountTowardRest) that is exactly its rate: the engine only decrements it in D.
     *
     * Without onDutyCountTowardRest the clock is `betweenRestLimit − (now − endOfLastBreak)`,
     * which falls at −1 in every status. So `false` there does not mean frozen — but it does not
     * mean unknown either, which is what this used to answer. A rate the sentence above states
     * exactly is `draining`; answering `unknown` made the floor hand back a coverage gap at
     * `state.nextDate`, reachable on 9 of the 142 rulesets and covered by no FACTOR_LIMITATIONS
     * entry, so nothing would have caught it. The jump that does lift this value is the break
     * qualifying, and `availabilityFactors()` already names it as `breakQualify`; the envelope
     * pairs the two. See AvailabilityCalculator.underlyingTrajectory (MYG-139401).
     *
     * The break-opening instant needs one extra thing to make this shape safe — see the
     * Emergency Response carve-out in availabilityFactors(), which names breakQualify there so the
     * floor is not alone in the map with nothing to bound it.
     */
    protected override underlyingTrajectory(state: IState): TUnderlyingTrajectory {
        return this.conditions(state).isOnDutyCountTowardRest ? super.underlyingTrajectory(state) : DRAINING;
    }

    exhaustion(state: IState): Date | undefined {
        const { avail, isConsuming } = this.conditions(state);

        if (avail === undefined || !isConsuming) {
            return undefined;
        }

        // When driving: availability is decreasing, predict when it hits zero.
        // Guard avail > 0: raw value may be ≤ 0 when already in violation —
        // no future zero-crossing exists in that case.
        if (avail > 0) {
            return new Date(state.nextDate.getTime() + avail);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);

        if (c.avail === undefined) {
            return undefined;
        }

        if (c.isConsuming) {
            // When driving: availability is decreasing. There are no genuine inflections.
            // Exhaustion is handled by the base class.
            return {};
        }

        // When not driving: availability is frozen, predict when break would qualify

        // No restBreakMinimum configured: the break-qualification concept does not exist for
        // this ruleset. No inflection is approaching while off-duty, so no event is predictable.
        if (!c.restBreakMinimum) {
            return undefined;
        }

        // No break running at all.
        if (c.breakStartTime === undefined || c.effectiveBreakMinimum === undefined) {
            return undefined;
        }

        // The break began at this very log, so no time has accrued into the counter yet. Where the
        // break clock is timeUntilMustRest the anchor still carries the whole prediction, but
        // without onDutyCountTowardRest the clock falls at −1 in every status while isConsuming
        // reports only driving (see underlyingTrajectory): naming an inflection from the anchor would open
        // a window this calculator already describes at the wrong rate. Leave that family as it
        // was and re-evaluate at the next log boundary.
        //
        // Unless the Emergency Response floor is governing this type (MYG-139401). The objection
        // above is entirely about isConsuming under-reporting the rate, and under the floor there
        // is nothing to under-report: the reported value IS the time left in the day, compute()
        // forces isConsuming true, and −1 is exactly right. So the window this opens is described
        // at the correct rate, which is the one condition the early return was protecting.
        //
        // It also has to be named here, not merely permitted. availabilityFactors() is otherwise
        // empty in this state, so the floor would be alone in the map with nothing to bound it —
        // and the break qualifying is precisely what ends the floor's governance, by lifting the
        // value to restBreakMaximum, above the floor. Left unnamed the floor reports the day
        // boundary and is late by the whole remainder of the break (measured: 2 h on
        // CaliforniaFlammableLiquidWithRestRequirement), which is the direction that serves a
        // stale clock.
        if (!c.consecutiveBreakTime && !c.isOnDutyCountTowardRest && !floorGoverns(state, c.avail)) {
            return undefined;
        }

        // Break already qualified: no approaching inflection while off-duty.
        if ((c.consecutiveBreakTime ?? 0) >= c.restBreakMinimum) {
            return undefined;
        }

        // Rest is already at the value a credit restores it to, so the credit is a no-op and
        // reporting it would be a spurious factor. Reachable two ways: the workday reset sets
        // timeUntilMustRest to the maximum before any break starts, and earlyRestBreaks credits
        // before consecutiveBreakTime ever reaches restBreakMinimum.
        if (c.restBreakMaximum !== undefined && c.avail >= c.restBreakMaximum) {
            return undefined;
        }

        const breakQualifyDate = new Date(c.breakStartTime.getTime() + c.effectiveBreakMinimum);

        return {
            breakQualify: { factor: () => breakQualifyDate, label: "break qualifies" },
        };
    }
}

export const extractRestConditions = (state: IState): RestConditions => {
    return new RestCalculator().extractConditions(state) as RestConditions;
};

export const computeRestResult = (state: IState): TComputedAvailabilityResult | undefined => {
    return new RestCalculator().compute(state);
};
